package fr.frux.shared.domain.model

import fr.frux.core.maps.MapBounds
import kotlin.math.floor
import kotlin.time.Instant

/**
 * Pyramide de regroupements précalculée par le serveur (#622, ADR 0039). [points] porte tous les
 * niveaux de zoom, de 0 à [maxZoom], et [detailZoom] est le zoom à partir duquel la carte retombe
 * sur le lot détaillé.
 */
data class ClusterPyramid(
    val maxZoom: Int,
    val detailZoom: Int,
    val generatedAt: Instant,
    val points: List<ClusterPoint>,
) {
    // Groupé une fois : le comptage est rappelé à chaque mouvement de caméra.
    val levels: Map<Int, List<ClusterPoint>> by lazy { points.groupBy { it.zoom } }
}

/** Un amas ([count] supérieur à 1) ou un arbre isolé ([tree] renseigné) d'un niveau [zoom]. */
data class ClusterPoint(
    val latitude: Double,
    val longitude: Double,
    val zoom: Int,
    val count: Int,
    val expansionZoom: Int?,
    val species: List<SpeciesCount>,
    val tree: IsolatedTree? = null,
)

data class IsolatedTree(
    val id: Long,
    val speciesId: Long,
    val status: TreeStatus,
    val lastObservedAt: Instant?,
    val needsCheck: Boolean,
    val locked: Boolean,
)

data class SpeciesCount(
    val speciesId: Long,
    val count: Int,
)

/**
 * Nombre d'arbres dans [bounds] au niveau de [zoom]. Somme les [ClusterPoint.count] et non le nombre
 * de points, car un amas en représente plusieurs. Rend 0 sans rectangle visible.
 */
fun ClusterPyramid.countInView(
    zoom: Float,
    bounds: MapBounds?,
): Int {
    if (bounds == null) return 0
    val level = floor(zoom).toInt().coerceIn(0, maxZoom)
    return levels[level].orEmpty().sumOf { point ->
        val inside =
            point.latitude in bounds.southwest.latitude..bounds.northeast.latitude &&
                point.longitude in bounds.southwest.longitude..bounds.northeast.longitude
        if (inside) point.count else 0
    }
}
